/**
 * VendorList.tsx
 * Shows ConfigUploadPanel inline when vendor list is empty.
 * Once upload succeeds the list refreshes and the panel disappears.
 */

import { message, Popconfirm, Table, Alert } from 'antd';
import type { ButtonProps, SelectProps, TablePaginationConfig } from 'antd';
import { PAGE_SIZE_OPTIONS } from '@constants/pagination';
import { useNavigate } from 'react-router-dom';
import { useVendorList } from './useVendorList';
import PageLayout from '@components/layout/PageLayout';
import styles from './VendorList.module.css';
import { NSPButton } from '@components/ui/NSPButton';
import ConfigUploadPanel from '@components/configupload/configuploadpanel'; // ← updated import
import { testPaginationItemRender, withTestColumnAttrs, type TestAttrs } from '@utils/testAttr';

export default function VendorList() {
  const vendorHook = useVendorList();
  const navigate = useNavigate();

  const handleAdd = () => navigate('/vendor-list/new');
  const handleEdit = (record: any) => navigate(`/vendor-list/edit/${record.id}`);

  const handleDelete = async (id: number) => {
    const result = await vendorHook.handleDeleteVendor(id);
    if (result.success) message.success('Vendor deleted successfully');
    else message.error(`Failed to delete vendor: ${result.error}`);
  };

  const handleUploadComplete = () => {
    vendorHook.refetch();
    message.success('Config upload completed. Vendor list refreshed.', 5);
  };

  // Show upload panel only when list is empty and not loading
  const showUploadPanel = !vendorHook.loading && vendorHook.totalCount === 0;

  const columns = [
    {
      title: 'Vendor Name', dataIndex: 'vendorName', key: 'vendorName',
      sorter: (a: any, b: any) => (a.vendorName || '').localeCompare(b.vendorName || ''),
    },
    {
      title: 'Contact Info', dataIndex: 'vendorContactNumber', key: 'vendorContactNumber',
      sorter: (a: any, b: any) => (a.vendorContactNumber || '').localeCompare(b.vendorContactNumber || ''),
    },
    {
      title: 'Address', dataIndex: 'vendorAddress', key: 'vendorAddress', ellipsis: true,
      sorter: (a: any, b: any) => (a.vendorAddress || '').localeCompare(b.vendorAddress || ''),
    },
    {
      title: 'Email', dataIndex: 'vendorEmailId', key: 'vendorEmailId', ellipsis: true,
      sorter: (a: any, b: any) => (a.vendorEmailId || '').localeCompare(b.vendorEmailId || ''),
    },
    {
      title: (<>Purchase<br />Order No.</>), dataIndex: 'purchaseOrderNo', key: 'purchaseOrderNo',
      sorter: (a: any, b: any) => (a.purchaseOrderNo || '').localeCompare(b.purchaseOrderNo || ''),
    },
    {
      title: (<>Need Details<br />With Invoice</>), dataIndex: 'needDetailsWithInvoice', key: 'needDetailsWithInvoice',
      render: (val: string | boolean) => (val === true || val === 'Yes' ? 'Yes' : 'No'),
    },
    {
      title: 'Actions', key: 'actions', width: 150,
      render: (_: any, record: any) => (
        <div className={styles.actionButtons}>
          <NSPButton size="small" onClick={() => handleEdit(record)} data-testid="edit-vendor-button">Edit</NSPButton>
          <Popconfirm
            title="Are you sure to delete this vendor?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes" cancelText="No" placement="topRight"
            okButtonProps={{ 'data-testid': 'popconfirm-ok-button' } as ButtonProps}
            cancelButtonProps={{ 'data-testid': 'popconfirm-cancel-button' } as ButtonProps}
          >
            <NSPButton size="small" danger data-testid="delete-vendor-button">Delete</NSPButton>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <PageLayout
      title={
        <>
          Vendor List
          {vendorHook.totalCount > 0 && (
            <span className={styles.vendorCount} data-testid="vendor-count" data-count={String(vendorHook.totalCount)}>({vendorHook.totalCount} vendors)</span>
          )}
        </>
      }
      headerAccessoryView={
        showUploadPanel ? null : (
          <NSPButton onClick={handleAdd} data-testid="add-vendor-button">Add New Vendor</NSPButton>
        )
      }
    >
      <div className={styles.content}>

        {vendorHook.saveFeedback && (
          <Alert
            message={vendorHook.saveFeedback.status === 'success' ? 'Save Successful!' : 'Save Failed!'}
            description={vendorHook.saveFeedback.message}
            type={vendorHook.saveFeedback.status}
            showIcon closable
            style={{ marginBottom: 16 }}
            data-testid="save-feedback-alert"
            data-type={vendorHook.saveFeedback.status}
          />
        )}

        {/* Config upload panel – shown only when list is empty */}
        {showUploadPanel && (
          <div data-testid="config-upload-panel">
            <ConfigUploadPanel onUploadComplete={handleUploadComplete} />
          </div>
        )}

        {/* Vendor table – hidden while upload panel is showing */}
        {!showUploadPanel && (
          <Table
            dataSource={vendorHook.data}
            columns={withTestColumnAttrs(columns, 'vendor-value', {
              purchaseOrderNo: 'Purchase Order No',
              needDetailsWithInvoice: 'Need Details With Invoice',
            })}
            rowKey="id"
            loading={vendorHook.loading}
            data-testid="vendor-table"
            data-loading={String(vendorHook.loading)}
            onRow={(record: any) =>
              ({ 'data-testid': 'vendor-row', 'data-vendor-id': String(record.id), 'data-vendor-name': record.vendorName ?? '' }) as TestAttrs
            }
            tableLayout="fixed"
            pagination={{
              current: vendorHook.currentPage,
              pageSize: vendorHook.pageSize,
              total: vendorHook.totalCount,
              showSizeChanger: { 'data-testid': 'page-size-select' } as SelectProps,
              pageSizeOptions: PAGE_SIZE_OPTIONS,
              onChange: vendorHook.onChangePage,
              showQuickJumper: false,
              showTotal: (total, range) => <span data-testid="pagination-total">{`${range[0]}-${range[1]} of ${total} vendors`}</span>,
              itemRender: testPaginationItemRender,
              'data-testid': 'table-pagination',
            } as TablePaginationConfig}
            scroll={{ x: 'max-content' }}
          />
        )}

      </div>
    </PageLayout>
  );
}